import { useState } from "react";
import { Hammer, Wrench, Ruler, Cog, Paintbrush, Plug } from "lucide-react";
import ScoreSubmit from "@/components/landing/games/ScoreSubmit";

const ICONS = [Hammer, Wrench, Ruler, Cog, Paintbrush, Plug];

function shuffledDeck() {
  return [...ICONS, ...ICONS]
    .map((Icon, i) => ({ Icon, key: i }))
    .sort(() => Math.random() - 0.5);
}

export default function ToolMatch() {
  const [deck, setDeck] = useState(shuffledDeck);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const won = matched.length === deck.length;

  const reset = () => {
    setDeck(shuffledDeck());
    setFlipped([]);
    setMatched([]);
    setMoves(0);
  };

  const flip = (idx) => {
    if (won || flipped.includes(idx) || matched.includes(idx) || flipped.length === 2) return;
    const next = [...flipped, idx];
    setFlipped(next);
    if (next.length === 2) {
      setMoves((m) => m + 1);
      const [a, b] = next;
      if (deck[a].Icon === deck[b].Icon) {
        setTimeout(() => {
          setMatched((m) => [...m, a, b]);
          setFlipped([]);
        }, 450);
      } else {
        setTimeout(() => setFlipped([]), 700);
      }
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E6DCD3] p-6 sm:p-8 shadow-sm text-center" data-testid="tool-match-game">
      <div className="flex items-center justify-between mb-4">
        <div className="text-left">
          <h3 className="font-bold text-base text-[#1E252B]">Toolbox Memory Match</h3>
          <p className="text-xs text-[#736B63] mt-0.5">Pair up every tool in as few moves as you can.</p>
        </div>
        <span className="text-xs font-bold text-[#D4691E]" data-testid="match-moves">Moves: {moves}</span>
      </div>

      <div className="grid grid-cols-4 gap-2.5 max-w-sm mx-auto">
        {deck.map(({ Icon, key }, idx) => {
          const shown = flipped.includes(idx) || matched.includes(idx);
          return (
            <button
              key={key}
              type="button"
              onClick={() => flip(idx)}
              data-testid={`match-card-${idx}`}
              className={`aspect-square rounded-xl border-2 flex items-center justify-center transition-colors ${
                matched.includes(idx)
                  ? "bg-[#FFF3EA] border-[#D4691E]"
                  : shown
                    ? "bg-white border-[#D4691E]"
                    : "bg-[#1E252B] border-[#1E252B] hover:bg-[#28323A]"
              }`}
            >
              {shown ? (
                <Icon className="w-6 h-6 text-[#D4691E]" />
              ) : (
                <span className="w-2 h-2 rounded-full bg-[#3A4752]" />
              )}
            </button>
          );
        })}
      </div>

      {won && (
        <div className="mt-5" data-testid="match-win">
          <p className="text-sm font-semibold text-[#1E252B]">
            Toolbox organized in {moves} moves — I keep mine just as tidy on every job.
          </p>
          <div className="flex items-center justify-center gap-3 mt-3">
            <button type="button" onClick={reset} data-testid="match-retry-btn" className="px-5 py-2 rounded bg-[#1E252B] hover:bg-[#D4691E] text-white text-xs font-bold transition-colors">
              Shuffle Again
            </button>
            <a href="#contact" data-testid="match-quote-btn" className="px-5 py-2 rounded bg-[#D4691E] hover:bg-[#A94E10] text-white text-xs font-bold transition-colors">
              Get a Quote
            </a>
          </div>
          <ScoreSubmit game="tool-match" score={moves} scoreLabel="moves" />
        </div>
      )}
    </div>
  );
}
